import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Radio } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { MOTIVOS_DE_RESOLUCAO } from '@/data/comunicacao';
import { dados } from '@/data/porta';
import type { Conversa } from '@/data/tipos';

export interface ResolverConversaModalProps {
    conversa: Conversa;
    onFechar: () => void;
    onResolvida: (conversa: Conversa) => void;
}

const OUTRO = '__outro__';

/**
 * Resolver a conversa, com o motivo (opcional). Quem resolveu e quando, o
 * servidor registra (0160). Se o paciente voltar a escrever, a conversa volta
 * para Pendentes sozinha.
 */
export function ResolverConversaModal({ conversa, onFechar, onResolvida }: ResolverConversaModalProps) {
    const aviso = useAviso();
    const nome = useId();
    const idOutro = useId();
    const [escolha, setEscolha] = useState('');
    const [outro, setOutro] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [resolvendo, setResolvendo] = useState(false);

    const motivo = escolha === OUTRO ? outro.trim() : escolha;

    const resolver = async () => {
        if (escolha === OUTRO && !motivo) {
            setErro('Escreva o motivo, ou escolha um da lista.');
            return;
        }
        setResolvendo(true);
        setErro(null);
        try {
            const resolvida = await dados().resolverConversa(conversa.id, motivo || undefined);
            aviso({ title: 'Conversa resolvida', description: motivo ? `${conversa.nome} · ${motivo}` : conversa.nome });
            onResolvida(resolvida);
            onFechar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível resolver a conversa.');
        } finally {
            setResolvendo(false);
        }
    };

    return (
        <Modal
            open width={460} onClose={onFechar}
            title={`Resolver a conversa com ${conversa.nome}?`}
            description="Ela vai para Resolvidos. Se a pessoa escrever de novo, volta para Pendentes."
            dirty={Boolean(escolha)}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => void resolver()} loading={resolvendo}>Resolver</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <fieldset style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <legend style={{ marginBottom: 8, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Motivo <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(opcional)</span>
                    </legend>
                    <Radio name={nome} checked={escolha === ''} onChange={() => { setEscolha(''); setErro(null); }} label="Sem motivo" />
                    {MOTIVOS_DE_RESOLUCAO.map((m) => (
                        <Radio key={m} name={nome} checked={escolha === m} onChange={() => { setEscolha(m); setErro(null); }} label={m} />
                    ))}
                    <Radio name={nome} checked={escolha === OUTRO} onChange={() => { setEscolha(OUTRO); setErro(null); }} label="Outro" />
                </fieldset>
                {escolha === OUTRO && (
                    <FormField label="Qual motivo?" required htmlFor={idOutro}>
                        <Input id={idOutro} value={outro} onChange={(v) => { setOutro(v); setErro(null); }} maxLength={300} placeholder="Ex.: Encaminhado para outra clínica" />
                    </FormField>
                )}
            </div>
        </Modal>
    );
}
